import { Head, Link, useForm } from "@inertiajs/react"
import { Building2, ChevronRight, Plus } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  name: string
  slug: string
  onboarding_state: string
  created_at: string
}

interface Props {
  customers: Customer[]
}

// Maps an onboarding_state to a badge tone. Unknown states fall through to a
// neutral outline so a new backend state never renders blank.
const STATE_TONE: Record<string, string> = {
  active: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400",
  onboarding: "border-blue-500/40 text-blue-600 dark:text-blue-400",
  pending: "border-amber-500/40 text-amber-600 dark:text-amber-400",
  archived: "border-slate-400/40 text-slate-600 dark:text-slate-400",
}

function stateLabel(state: string) {
  return state.replace(/_/g, " ")
}

export default function Customers({ customers }: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Customers", href: routes.customersPath() },
  ]

  const form = useForm({ name: "" })

  function addCustomer(e: React.FormEvent) {
    e.preventDefault()
    form.post(routes.customersPath(), {
      onSuccess: () => form.reset("name"),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Customers" />

      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-muted-foreground text-sm">
            {customers.length} customer{customers.length !== 1 && "s"}
          </p>
        </div>

        <Card>
          <CardContent className="p-5">
            <form
              onSubmit={addCustomer}
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
            >
              <div className="flex-1 space-y-2">
                <Label htmlFor="name">Add customer</Label>
                <Input
                  id="name"
                  value={form.data.name}
                  onChange={(e) => form.setData("name", e.target.value)}
                  placeholder="Customer name"
                />
                {form.errors.name && (
                  <p className="text-destructive text-sm">{form.errors.name}</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={form.processing || form.data.name.trim().length === 0}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </form>
          </CardContent>
        </Card>

        {customers.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center">
            No customers yet.
          </p>
        ) : (
          <div className="space-y-3">
            {customers.map((customer) => (
              <Link
                key={customer.id}
                href={routes.orgRootPath(customer.slug)}
                className="focus-visible:ring-ring block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                <Card className="group hover:border-primary/50 cursor-pointer transition-all hover:shadow-md">
                  <CardContent className="flex items-center justify-between gap-3 p-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                        <Building2 className="text-primary h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold">
                          {customer.name}
                        </p>
                        <p className="text-muted-foreground truncate font-mono text-xs">
                          /{customer.slug}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          STATE_TONE[customer.onboarding_state] ??
                          "border-slate-400/40"
                        }`}
                      >
                        {stateLabel(customer.onboarding_state)}
                      </Badge>
                      <ChevronRight className="text-muted-foreground h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
